import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Activity, RefreshCw, Info, Loader2, Gauge } from 'lucide-react';
import api from '../utils/axios';
import { ActividadKpis } from '../components/actividad/ActividadKpis';
import { ActividadChart } from '../components/actividad/ActividadChart';
import { TopSecciones } from '../components/actividad/TopSecciones';
import { UsuariosActividadTabla } from '../components/actividad/UsuariosActividadTabla';
import { EventosFeed } from '../components/actividad/EventosFeed';
import { UsuarioActividadSheet } from '../components/actividad/UsuarioActividadSheet';
import { PERIODOS, fmtFechaHora } from '../components/actividad/actividadUtils';

const VISTAS = [{ k: 'usuarios', l: 'Usuarios' }, { k: 'feed', l: 'Actividad reciente' }];

const Pills = ({ items, value, onChange, prefix }) => (
  <div className="flex rounded-full bg-white border border-slate-200 p-1">
    {items.map((p) => (
      <button key={p.k ?? p.v} onClick={() => onChange(p.k ?? p.v)} data-testid={`${prefix}-${p.k ?? p.v}`}
        className={`px-3.5 py-1 text-xs font-semibold rounded-full transition-colors duration-200 ${value === (p.k ?? p.v) ? 'bg-teal-700 text-white' : 'text-slate-600 hover:text-slate-900'}`}>
        {p.l}
      </button>
    ))}
  </div>
);

export default function ActividadUsuarios() {
  const [dias, setDias] = useState(30);
  const [vista, setVista] = useState('usuarios');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sel, setSel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/actividad-usuarios/resumen', { params: { dias } });
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.status === 403 ? 'No tenés permiso para ver esta sección.' : 'No se pudo cargar la actividad.');
    } finally { setLoading(false); }
  }, [dias]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="vig-scope p-6 lg:p-8 space-y-6 bg-slate-50 min-h-full" data-testid="actividad-usuarios-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">Configuración</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Activity className="h-9 w-9 text-teal-700" /> Actividad de Usuarios
          </h1>
          <p className="text-sm text-slate-500 mt-1">Quién se conecta y quién no, qué secciones usa cada uno, qué acciones hace y desde dónde entra.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/m/configuracion/procesos-automaticos" className="text-sm font-semibold text-teal-700 hover:text-teal-900 flex items-center gap-1.5 mr-2" data-testid="act-link-procesos">
            <Gauge size={15} /> Procesos automáticos
          </Link>
          <Pills items={PERIODOS} value={dias} onChange={setDias} prefix="act-periodo" />
          <Button variant="outline" onClick={load} disabled={loading} data-testid="act-actualizar-btn">
            <RefreshCw size={16} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
        </div>
      </div>

      {error && <p className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="act-error">{error}</p>}
      {!data && loading && <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>}

      {data && (
        <>
          <p className="flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-xs text-sky-900" data-testid="act-nota-registro">
            <Info size={14} className="mt-0.5 shrink-0" />
            {data.registro_desde
              ? `Desde el ${fmtFechaHora(data.registro_desde)} se registran los ingresos, las pantallas y los dispositivos. Las acciones anteriores salen del historial de auditoría que ya existía.`
              : 'Los ingresos, pantallas y dispositivos se empiezan a registrar desde ahora. Las acciones anteriores salen del historial de auditoría que ya existía.'}
          </p>
          <ActividadKpis kpis={data.kpis} dias={dias} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ActividadChart serie={data.por_dia} />
            <TopSecciones items={data.top_secciones} />
          </div>
          <Pills items={VISTAS} value={vista} onChange={setVista} prefix="act-vista" />
          {vista === 'usuarios'
            ? <UsuariosActividadTabla usuarios={data.usuarios} dias={dias} onVer={setSel} />
            : <EventosFeed usuarios={data.usuarios} dias={dias} />}
        </>
      )}
      <UsuarioActividadSheet usuario={sel} dias={dias} onOpenChange={(o) => !o && setSel(null)} />
    </div>
  );
}
